import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { api } from '../services/api';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  color: #0a0e17;
  border: none;
  padding: 0.7rem 1rem;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
`;

const Filters = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;

const TabBar = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const TabButton = styled.button<{ $active?: boolean }>`
  padding: 0.55rem 0.9rem;
  border-radius: 999px;
  border: 1px solid ${props => props.$active ? '#0f172a' : '#e2e8f0'};
  background: ${props => props.$active ? '#0f172a' : '#ffffff'};
  color: ${props => props.$active ? '#ffffff' : '#0f172a'};
  font-weight: 700;
  cursor: pointer;
`;

const SectionCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 1.2rem;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.06);
  margin-bottom: 1.4rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
`;

const SectionDescription = styled.p`
  margin: 0.25rem 0 0;
  color: #64748b;
  font-size: 0.9rem;
`;

const SearchInput = styled.input`
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  border: 1px solid #d6dae0;
  min-width: 220px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  table-layout: fixed;

  th, td {
    text-align: left;
    padding: 0.75rem 0.85rem;
    font-size: 0.92rem;
    vertical-align: middle;
  }

  th {
    color: #64748b;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  td {
    border-bottom: 1px solid #f1f5f9;
    color: #0f172a;
  }

  th:last-child,
  td:last-child {
    text-align: right;
    width: 240px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: nowrap;
  justify-content: flex-end;
  white-space: nowrap;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 1.25rem;
  flex-wrap: wrap;
`;

const PageInfo = styled.span`
  color: #64748b;
  font-size: 0.9rem;
`;

const PageControls = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 0.45rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: ${props => props.$active ? '#0f172a' : '#f8fafc'};
  color: ${props => props.$active ? '#fff' : '#475569'};
  font-weight: 600;
  cursor: pointer;
`;

const PageSizeSelect = styled.select`
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-weight: 600;
  color: #475569;
`;

const Ellipsis = styled.span`
  padding: 0.45rem 0.6rem;
  color: #94a3b8;
  font-weight: 600;
`;

const InfoBanner = styled.div`
  background: #ecfeff;
  border: 1px solid #a5f3fc;
  color: #0f172a;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
`;

const GhostButton = styled.button`
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
`;

const DangerButton = styled.button`
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
`;

const StatusBadge = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  background: ${props => props.$active ? 'rgba(0, 212, 170, 0.15)' : '#f1f5f9'};
  color: ${props => props.$active ? '#0f766e' : '#64748b'};
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 50;
  backdrop-filter: blur(3px);
`;

const ModalContent = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 18px;
  padding: 1.75rem;
  width: min(720px, 92vw);
  max-height: 90vh;
  overflow-y: auto;
  z-index: 60;
  border: 1px solid #e2e8f0;
  box-shadow: 0 28px 60px rgba(15, 23, 42, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
`;

const ModalTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: #0f172a;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const Section = styled.div`
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  padding: 1.1rem;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
`;

const SectionTitle = styled.h3`
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin: 0 0 0.85rem;
`;

const FieldGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div<{ $span?: number }>`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  grid-column: span ${props => props.$span ?? 1};

  @media (max-width: 720px) {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
`;

const Input = styled.input`
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  border: 1px solid #d6dae0;
  font-size: 0.95rem;
  background: #ffffff;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);

  &:focus {
    outline: none;
    border-color: #00b894;
    box-shadow: 0 0 0 3px rgba(0, 212, 170, 0.18);
  }
`;

const TextArea = styled.textarea`
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  border: 1px solid #d6dae0;
  font-size: 0.95rem;
  min-height: 140px;
  resize: vertical;
  background: #ffffff;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);

  &:focus {
    outline: none;
    border-color: #00b894;
    box-shadow: 0 0 0 3px rgba(0, 212, 170, 0.18);
  }
`;

const Select = styled.select`
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  border: 1px solid #d6dae0;
  font-size: 0.95rem;
  background: #ffffff;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);

  &:focus {
    outline: none;
    border-color: #00b894;
    box-shadow: 0 0 0 3px rgba(0, 212, 170, 0.18);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.35rem;
  cursor: pointer;
  color: #64748b;
  width: 36px;
  height: 36px;
  border-radius: 50%;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`;

const PreviewBox = styled.div`
  border: 1px dashed #d6dae0;
  border-radius: 12px;
  padding: 1rem;
  background: #f8fafc;
`;

const PreviewTitle = styled.h3`
  font-size: 0.95rem;
  color: #334155;
  margin-bottom: 0.6rem;
`;

const PreviewBody = styled.div`
  font-size: 0.95rem;
  color: #0f172a;
  line-height: 1.6;
`;

const RuleRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1.2fr) minmax(140px, 1fr) auto;
  gap: 0.6rem;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const TinyButton = styled.button`
  background: #f8fafc;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  padding: 0.35rem 0.6rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
`;

const CompanyList = styled.div`
  display: grid;
  gap: 0.6rem;
  max-height: 240px;
  overflow-y: auto;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
`;

const CompanyItem = styled.label`
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  padding: 0.6rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
`;

const CompanyMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.85rem;
  color: #475569;
`;

type Template = {
  id: number;
  name: string;
  channel: string;
  subject?: string | null;
  body_html?: string | null;
  body_text?: string | null;
  is_active: boolean;
  created_at?: string | null;
};

type SegmentRule = {
  field: string;
  value: any;
};

type Segment = {
  id: number;
  name: string;
  description?: string | null;
  rules: SegmentRule[];
  is_active: boolean;
  created_at?: string | null;
};

type Campaign = {
  id: number;
  name: string;
  channel: string;
  status: string;
  scheduled_at?: string | null;
  total_recipients?: number;
  sent_count?: number;
  failed_count?: number;
  open_count?: number;
  click_count?: number;
  template?: Template | null;
  segment?: Segment | null;
  customer_segment?: Segment | null;
  created_at?: string | null;
};

type AutomationStep = {
  id: number;
  step_order: number;
  delay_minutes: number;
  channel: string;
  template_id: number;
  template?: Template | null;
};

type Automation = {
  id: number;
  name: string;
  trigger_type: string;
  trigger_config?: Record<string, any> | null;
  status: string;
  steps: AutomationStep[];
  created_at?: string | null;
};

type Company = {
  id: number;
  name: string;
  email?: string | null;
  cnpj?: string | null;
  plan?: { id: number; name: string } | null;
  user?: { id: number; name: string; email: string } | null;
};

const emptyForm = {
  name: '',
  channel: 'email',
  subject: '',
  body_html: '',
  body_text: '',
  is_active: true,
};

const createEmptySegmentForm = (defaultField: string) => ({
  name: '',
  description: '',
  rules: [{ field: defaultField, value: '' }] as SegmentRule[],
  is_active: true,
});

const emptyCampaignForm = {
  name: '',
  channel: 'email',
  template_id: '',
  segment_id: '',
  scheduled_at: '',
  audience_type: 'all',
  send_now: true,
};

const createEmptyAutomationForm = (audience: SegmentAudience) => ({
  name: '',
  trigger_type: audience === 'customers' ? 'inactivity' : 'user_registered',
  status: 'active',
  days_inactive: audience === 'customers' ? '30' : '7',
  days_before: '3',
  segment_id: '',
  customer_event: 'customer_birthday',
  custom_json: '',
});

const emptyStepForm = {
  step_order: 1,
  delay_minutes: 0,
  channel: 'email',
  template_id: '',
};

const userSegmentRuleOptions = [
  { value: 'created_days_ago_gte', label: 'Criado ha pelo menos (dias)' },
  { value: 'created_days_ago_lte', label: 'Criado nos ultimos (dias)' },
  { value: 'last_login_days_ago_gte', label: 'Ultimo login ha pelo menos (dias)' },
  { value: 'last_login_days_ago_lte', label: 'Ultimo login nos ultimos (dias)' },
  { value: 'last_login_missing', label: 'Sem login registrado' },
  { value: 'subscription_status_in', label: 'Status assinatura (in)' },
  { value: 'subscription_status_not_in', label: 'Status assinatura (not in)' },
  { value: 'has_store', label: 'Possui loja' },
  { value: 'has_company', label: 'Possui empresa' },
  { value: 'has_active_subscription', label: 'Assinatura ativa' },
  { value: 'has_pending_payment', label: 'Pagamento pendente' },
  { value: 'has_customer', label: 'Tem clientes' },
  { value: 'email_verified', label: 'Email verificado' },
  { value: 'company_id_in', label: 'Empresa ID (in)' },
  { value: 'company_plan_id_in', label: 'Plano ID (in)' },
  { value: 'company_plan_name_in', label: 'Plano nome (in)' },
  { value: 'user_id_in', label: 'Usuario ID (in)' },
];

const customerSegmentRuleOptions = [
  { value: 'is_active', label: 'Cliente ativo' },
  { value: 'total_points_gte', label: 'Pontos totais >= valor' },
  { value: 'total_points_lte', label: 'Pontos totais <= valor' },
  { value: 'recency_days_lte', label: 'Recencia: comprou nos ultimos (dias)' },
  { value: 'recency_days_gte', label: 'Recencia: sem compra ha (dias)' },
  { value: 'purchase_count_gte', label: 'Frequencia: compras totais >= valor' },
  { value: 'purchase_count_lte', label: 'Frequencia: compras totais <= valor' },
  { value: 'total_spent_gte', label: 'Monetario: gasto total >= valor' },
  { value: 'total_spent_lte', label: 'Monetario: gasto total <= valor' },
  { value: 'avg_ticket_gte', label: 'Monetario: ticket medio >= valor' },
  { value: 'avg_ticket_lte', label: 'Monetario: ticket medio <= valor' },
  { value: 'created_days_ago_gte', label: 'Cadastro ha pelo menos (dias)' },
  { value: 'created_days_ago_lte', label: 'Cadastro nos ultimos (dias)' },
  { value: 'last_purchase_days_ago_gte', label: 'Ultima compra ha pelo menos (dias)' },
  { value: 'last_purchase_days_ago_lte', label: 'Ultima compra nos ultimos (dias)' },
  { value: 'last_purchase_missing', label: 'Sem compra registrada' },
  { value: 'birthday_month_in', label: 'Mes de aniversario (in)' },
  { value: 'gender_in', label: 'Genero (in)' },
  { value: 'store_id_in', label: 'Loja ID (in)' },
  { value: 'has_email', label: 'Possui email' },
  { value: 'has_phone', label: 'Possui telefone' },
];

type SegmentAudience = 'users' | 'customers';

const userAutomationTriggerOptions = [
  { value: 'user_registered', label: 'Cadastro' },
  { value: 'user_logged_in', label: 'Login' },
  { value: 'subscription_started', label: 'Assinatura iniciada' },
  { value: 'subscription_cancelled', label: 'Assinatura cancelada' },
  { value: 'subscription_suspended', label: 'Assinatura suspensa' },
  { value: 'payment_failed', label: 'Pagamento falhou' },
  { value: 'trial_ending', label: 'Trial terminando' },
  { value: 'inactivity', label: 'Inatividade' },
  { value: 'custom', label: 'Custom' },
];

const customerAutomationTriggerOptions = [
  { value: 'inactivity', label: 'Inatividade de compra' },
  { value: 'custom', label: 'Evento do cliente' },
];

type CrmManagerProps = {
  apiPrefix?: string;
  allowCompanySelection?: boolean;
  segmentAudience?: SegmentAudience;
};

export const CrmManager: React.FC<CrmManagerProps> = ({
  apiPrefix = '/admin/crm',
  allowCompanySelection = true,
  segmentAudience = 'users',
}) => {
  const defaultSegmentField = segmentAudience === 'customers' ? 'total_points_gte' : 'created_days_ago_gte';
  const segmentEndpoint = segmentAudience === 'customers' ? 'customer-segments' : 'segments';
  const segmentRuleOptions = segmentAudience === 'customers' ? customerSegmentRuleOptions : userSegmentRuleOptions;
  const automationTriggerOptions = segmentAudience === 'customers' ? customerAutomationTriggerOptions : userAutomationTriggerOptions;
  const audienceLabelPlural = segmentAudience === 'customers' ? 'clientes' : 'usuarios';
  const booleanRuleFields = segmentAudience === 'customers'
    ? ['is_active', 'last_purchase_missing', 'has_email', 'has_phone']
    : ['has_store', 'has_company', 'has_active_subscription', 'has_pending_payment', 'has_customer', 'email_verified', 'last_login_missing'];

  const [activeTab, setActiveTab] = useState<'templates' | 'segments' | 'campaigns' | 'automations'>('templates');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [segmentForm, setSegmentForm] = useState(() => createEmptySegmentForm(defaultSegmentField));
  const [campaignForm, setCampaignForm] = useState(emptyCampaignForm);
  const [automationForm, setAutomationForm] = useState(() => createEmptyAutomationForm(segmentAudience));
  const [stepForm, setStepForm] = useState(emptyStepForm);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isSegmentModalOpen, setIsSegmentModalOpen] = useState(false);
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [isAutomationModalOpen, setIsAutomationModalOpen] = useState(false);
  const [isStepsModalOpen, setIsStepsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
  const [editingAutomation, setEditingAutomation] = useState<Automation | null>(null);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [editingStep, setEditingStep] = useState<AutomationStep | null>(null);
  const [activeAutomation, setActiveAutomation] = useState<Automation | null>(null);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadTemplates = useCallback(async () => {
    const response = await api.get(`${apiPrefix}/templates`);
    setTemplates(response.data.data || []);
  }, [apiPrefix]);

  const loadSegments = useCallback(async () => {
    const response = await api.get(`${apiPrefix}/${segmentEndpoint}`);
    const data = response.data.data || [];
    setSegments(data);
    return data;
  }, [apiPrefix, segmentEndpoint]);

  const loadCampaigns = useCallback(async () => {
    const response = await api.get(`${apiPrefix}/campaigns`);
    const data = response.data.data || [];
    setCampaigns(data);
    return data;
  }, [apiPrefix]);

  const loadAutomations = useCallback(async () => {
    const response = await api.get(`${apiPrefix}/automations`);
    const data = response.data.data || [];
    setAutomations(data);
    return data;
  }, [apiPrefix]);

  const loadCompanies = useCallback(async (searchTerm: string) => {
    const response = await api.get('/admin/companies', {
      params: {
        per_page: 50,
        search: searchTerm || undefined,
      },
    });
    const payload = response.data.data;
    setCompanies(payload?.data || []);
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (activeTab === 'segments') {
      loadSegments();
    }
    if (activeTab === 'campaigns') {
      loadSegments();
      loadCampaigns();
      if (allowCompanySelection) {
        loadCompanies('');
      }
    }
    if (activeTab === 'automations') {
      loadAutomations();
      loadSegments();
    }
  }, [activeTab, allowCompanySelection, loadSegments, loadCampaigns, loadCompanies, loadAutomations]);

  useEffect(() => {
    if (activeTab === 'campaigns' && allowCompanySelection && segmentAudience === 'users') {
      const delay = setTimeout(() => loadCompanies(companySearch), 300);
      return () => clearTimeout(delay);
    }
    return undefined;
  }, [companySearch, activeTab, allowCompanySelection, loadCompanies, segmentAudience]);

  const filteredTemplates = useMemo(() => {
    const base = templates.filter(template => {
      const matchesSearch = template.name.toLowerCase().includes(search.toLowerCase());
      const matchesChannel = channelFilter === 'all' || template.channel === channelFilter;
      const matchesStatus =
        statusFilter === 'all' || (statusFilter === 'active' ? template.is_active : !template.is_active);
      return matchesSearch && matchesChannel && matchesStatus;
    });

    const sorted = [...base];
    sorted.sort((a, b) => {
      if (sortBy === 'name_desc') {
        return b.name.localeCompare(a.name);
      }
      if (sortBy === 'date_asc') {
        return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
      }
      if (sortBy === 'date_desc') {
        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
      }
      return a.name.localeCompare(b.name);
    });
    return sorted;
  }, [templates, search, channelFilter, statusFilter, sortBy]);

  useEffect(() => {
    setPage(1);
  }, [search, channelFilter, statusFilter, sortBy, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedTemplates = filteredTemplates.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages: number[] = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) {
      pages.push(-1);
    }

    for (let pageIndex = start; pageIndex <= end; pageIndex += 1) {
      pages.push(pageIndex);
    }

    if (end < totalPages - 1) {
      pages.push(-1);
    }

    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setForm(emptyForm);
    setIsTemplateModalOpen(true);
  };

  const openEditTemplate = (template: Template) => {
    setEditingTemplate(template);
    setForm({
      name: template.name || '',
      channel: template.channel || 'email',
      subject: template.subject || '',
      body_html: template.body_html || '',
      body_text: template.body_text || '',
      is_active: template.is_active,
    });
    setIsTemplateModalOpen(true);
  };

  const closeTemplateModal = () => {
    setIsTemplateModalOpen(false);
    setEditingTemplate(null);
    setForm(emptyForm);
  };

  const handleTemplateSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (editingTemplate) {
      await api.put(`${apiPrefix}/templates/${editingTemplate.id}`, form);
    } else {
      await api.post(`${apiPrefix}/templates`, form);
    }

    closeTemplateModal();
    loadTemplates();
  };

  const handleDelete = async (templateId: number) => {
    if (!window.confirm('Deseja remover este template?')) return;
    await api.delete(`${apiPrefix}/templates/${templateId}`);
    loadTemplates();
  };

  const toggleActive = async (template: Template) => {
    await api.put(`${apiPrefix}/templates/${template.id}`, {
      is_active: !template.is_active,
    });
    loadTemplates();
  };

  const openNewSegment = () => {
    setEditingSegment(null);
    setSegmentForm(createEmptySegmentForm(defaultSegmentField));
    setIsSegmentModalOpen(true);
  };

  const openEditSegment = (segment: Segment) => {
    setEditingSegment(segment);
    setSegmentForm({
      name: segment.name || '',
      description: segment.description || '',
      rules: (segment.rules || []).map(rule => ({
        field: rule.field,
        value: Array.isArray(rule.value)
          ? rule.value.join(', ')
          : typeof rule.value === 'boolean'
            ? rule.value ? 'true' : 'false'
            : rule.value ?? '',
      })),
      is_active: segment.is_active,
    });
    setIsSegmentModalOpen(true);
  };

  const closeSegmentModal = () => {
    setIsSegmentModalOpen(false);
    setEditingSegment(null);
    setSegmentForm(createEmptySegmentForm(defaultSegmentField));
  };

  const normalizeSegmentRules = (rules: SegmentRule[]) => {
    return rules
      .filter(rule => rule.field)
      .map(rule => {
        let value: any = rule.value;

        if (segmentAudience === 'users' && [
          'subscription_status_in',
          'subscription_status_not_in',
          'company_id_in',
          'user_id_in',
          'company_plan_id_in',
          'company_plan_name_in',
        ].includes(rule.field)) {
          const items = String(value || '')
            .split(',')
            .map(item => item.trim())
            .filter(Boolean);
          if (['company_id_in', 'user_id_in', 'company_plan_id_in'].includes(rule.field)) {
            value = items.map(item => Number(item)).filter(item => !Number.isNaN(item));
          } else {
            value = items;
          }
        } else if (segmentAudience === 'customers' && [
          'birthday_month_in',
          'gender_in',
          'store_id_in',
        ].includes(rule.field)) {
          const items = String(value || '')
            .split(',')
            .map(item => item.trim())
            .filter(Boolean);
          if (['birthday_month_in', 'store_id_in'].includes(rule.field)) {
            value = items.map(item => Number(item)).filter(item => !Number.isNaN(item));
          } else {
            value = items;
          }
        } else if ([
          'created_days_ago_gte',
          'created_days_ago_lte',
          'last_login_days_ago_gte',
          'last_login_days_ago_lte',
          'total_points_gte',
          'total_points_lte',
          'recency_days_gte',
          'recency_days_lte',
          'purchase_count_gte',
          'purchase_count_lte',
          'last_purchase_days_ago_gte',
          'last_purchase_days_ago_lte',
        ].includes(rule.field)) {
          value = Number(value || 0);
        } else if ([
          'total_spent_gte',
          'total_spent_lte',
          'avg_ticket_gte',
          'avg_ticket_lte',
        ].includes(rule.field)) {
          value = Number.parseFloat(String(value || '0')) || 0;
        } else if (booleanRuleFields.includes(rule.field)) {
          value = value === true || value === 'true';
        }

        return {
          field: rule.field,
          value,
        };
      });
  };

  const handleSegmentSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const payload = {
      name: segmentForm.name,
      description: segmentForm.description,
      rules: normalizeSegmentRules(segmentForm.rules),
      is_active: segmentForm.is_active,
    };

    if (editingSegment) {
      await api.put(`${apiPrefix}/${segmentEndpoint}/${editingSegment.id}`, payload);
    } else {
      await api.post(`${apiPrefix}/${segmentEndpoint}`, payload);
    }

    closeSegmentModal();
    loadSegments();
  };

  const handleSegmentDelete = async (segmentId: number) => {
    if (!window.confirm('Deseja remover este segmento?')) return;
    await api.delete(`${apiPrefix}/${segmentEndpoint}/${segmentId}`);
    loadSegments();
  };

  const previewSegment = async (segmentId: number) => {
    const response = await api.get(`${apiPrefix}/${segmentEndpoint}/${segmentId}/preview`);
    const count = response.data.data?.count ?? 0;
    window.alert(`${audienceLabelPlural.charAt(0).toUpperCase() + audienceLabelPlural.slice(1)} no segmento: ${count}`);
  };

  const openNewCampaign = () => {
    setEditingCampaign(null);
    setCampaignForm({
      ...emptyCampaignForm,
      audience_type: allowCompanySelection ? emptyCampaignForm.audience_type : 'all',
    });
    setSelectedCompanyIds([]);
    setIsCampaignModalOpen(true);
  };

  const openEditCampaign = (campaign: Campaign) => {
    const selectedSegment = campaign.customer_segment || campaign.segment || null;
    setEditingCampaign(campaign);
    setCampaignForm({
      name: campaign.name || '',
      channel: campaign.channel || 'email',
      template_id: campaign.template?.id ? String(campaign.template.id) : '',
      segment_id: selectedSegment?.id ? String(selectedSegment.id) : '',
      scheduled_at: campaign.scheduled_at ? campaign.scheduled_at.slice(0, 16) : '',
      audience_type: selectedSegment ? 'segment' : 'all',
      send_now: false,
    });
    setSelectedCompanyIds([]);
    setIsCampaignModalOpen(true);
  };

  const closeCampaignModal = () => {
    setIsCampaignModalOpen(false);
    setEditingCampaign(null);
    setCampaignForm(emptyCampaignForm);
    setSelectedCompanyIds([]);
  };

  const handleCampaignSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!campaignForm.template_id) {
      window.alert('Selecione um template.');
      return;
    }

    let segmentId: number | null = null;
    if (campaignForm.audience_type === 'segment') {
      if (!campaignForm.segment_id) {
        window.alert('Selecione um segmento.');
        return;
      }
      segmentId = Number(campaignForm.segment_id);
    }

    if (campaignForm.audience_type === 'companies') {
      if (segmentAudience !== 'users') {
        window.alert('Selecao manual por empresa esta disponivel apenas para segmentos de usuarios.');
        return;
      }
      if (!allowCompanySelection) {
        window.alert('Selecao de empresas nao disponivel neste perfil.');
        return;
      }
      if (selectedCompanyIds.length === 0) {
        window.alert('Selecione pelo menos uma empresa.');
        return;
      }
      const segmentResponse = await api.post(`${apiPrefix}/${segmentEndpoint}`, {
        name: `Selecao manual - ${campaignForm.name || 'Campanha'}`,
        description: 'Segmento criado automaticamente para envio manual.',
        rules: [
          {
            field: 'company_id_in',
            value: selectedCompanyIds,
          },
        ],
        is_active: true,
      });
      segmentId = segmentResponse.data.data?.id ?? null;
    }

    if (campaignForm.audience_type === 'all' && segmentAudience === 'customers') {
      const segmentResponse = await api.post(`${apiPrefix}/${segmentEndpoint}`, {
        name: `Todos os clientes - ${campaignForm.name || 'Campanha'}`,
        description: 'Segmento criado automaticamente para envio de campanha em massa para clientes ativos.',
        rules: [
          {
            field: 'is_active',
            value: true,
          },
        ],
        is_active: true,
      });
      segmentId = segmentResponse.data.data?.id ?? null;
    }

    const scheduledAt = campaignForm.send_now
      ? new Date().toISOString()
      : campaignForm.scheduled_at || null;

    const payload = {
      name: campaignForm.name,
      channel: campaignForm.channel,
      template_id: Number(campaignForm.template_id),
      segment_id: segmentAudience === 'users' ? segmentId : null,
      customer_segment_id: segmentAudience === 'customers' ? segmentId : null,
      scheduled_at: scheduledAt,
    };

    if (editingCampaign) {
      await api.put(`${apiPrefix}/campaigns/${editingCampaign.id}`, payload);
    } else {
      await api.post(`${apiPrefix}/campaigns`, payload);
    }

    closeCampaignModal();
    loadCampaigns();
  };

  const handleCampaignDelete = async (campaignId: number) => {
    if (!window.confirm('Deseja remover esta campanha?')) return;
    await api.delete(`${apiPrefix}/campaigns/${campaignId}`);
    loadCampaigns();
  };

  const scheduleCampaign = async (campaignId: number, scheduledAt?: string | null) => {
    await api.post(`${apiPrefix}/campaigns/${campaignId}/schedule`, {
      scheduled_at: scheduledAt || new Date().toISOString(),
    });
    loadCampaigns();
  };

  const openNewAutomation = () => {
    setEditingAutomation(null);
    setAutomationForm(createEmptyAutomationForm(segmentAudience));
    setIsAutomationModalOpen(true);
  };

  const openEditAutomation = (automation: Automation) => {
    const config = automation.trigger_config || {};
    setEditingAutomation(automation);
    setAutomationForm({
      name: automation.name,
      trigger_type: automation.trigger_type,
      status: automation.status,
      days_inactive: String(config.days_inactive ?? 7),
      days_before: String(config.days_before ?? 3),
      segment_id: '',
      customer_event: String(config.event ?? 'customer_birthday'),
      custom_json: automation.trigger_type === 'custom' ? JSON.stringify(config, null, 2) : '',
    });
    setIsAutomationModalOpen(true);
  };

  const closeAutomationModal = () => {
    setIsAutomationModalOpen(false);
    setEditingAutomation(null);
    setAutomationForm(createEmptyAutomationForm(segmentAudience));
  };

  const handleAutomationSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const triggerConfig: Record<string, any> = {};
    if (segmentAudience === 'customers') {
      if (!['inactivity', 'custom'].includes(automationForm.trigger_type)) {
        window.alert('No modo de clientes, use gatilhos de inatividade ou evento do cliente.');
        return;
      }
      triggerConfig.audience = 'customers';
    }
    if (automationForm.trigger_type === 'inactivity') {
      triggerConfig.days_inactive = Number(automationForm.days_inactive || 7);
    }
    if (automationForm.trigger_type === 'trial_ending') {
      triggerConfig.days_before = Number(automationForm.days_before || 3);
    }

    if (automationForm.segment_id) {
      const segment = segments.find(item => item.id === Number(automationForm.segment_id));
      if (segment) {
        triggerConfig.segment_rules = segment.rules;
      }
    }

    if (automationForm.trigger_type === 'custom' && automationForm.custom_json && segmentAudience === 'users') {
      try {
        const customConfig = JSON.parse(automationForm.custom_json);
        Object.assign(triggerConfig, customConfig);
      } catch (error) {
        window.alert('JSON invalido na configuracao custom.');
        return;
      }
    } else if (automationForm.trigger_type === 'custom' && segmentAudience === 'customers') {
      triggerConfig.event = automationForm.customer_event || 'customer_birthday';
    }

    const payload = {
      name: automationForm.name,
      trigger_type: automationForm.trigger_type,
      trigger_config: triggerConfig,
      status: automationForm.status,
    };

    if (editingAutomation) {
      await api.put(`${apiPrefix}/automations/${editingAutomation.id}`, payload);
    } else {
      await api.post(`${apiPrefix}/automations`, payload);
    }

    closeAutomationModal();
    loadAutomations();
  };

  const handleAutomationDelete = async (automationId: number) => {
    if (!window.confirm('Deseja remover esta automacao?')) return;
    await api.delete(`${apiPrefix}/automations/${automationId}`);
    loadAutomations();
  };

  const toggleAutomationStatus = async (automation: Automation) => {
    const nextStatus = automation.status === 'active' ? 'paused' : 'active';
    await api.put(`${apiPrefix}/automations/${automation.id}`, {
      status: nextStatus,
    });
    loadAutomations();
  };

  const openStepsModal = (automation: Automation) => {
    setActiveAutomation(automation);
    setEditingStep(null);
    setStepForm(emptyStepForm);
    setIsStepsModalOpen(true);
  };

  const closeStepsModal = () => {
    setIsStepsModalOpen(false);
    setActiveAutomation(null);
    setEditingStep(null);
    setStepForm(emptyStepForm);
  };

  const openEditStep = (step: AutomationStep) => {
    setEditingStep(step);
    setStepForm({
      step_order: step.step_order,
      delay_minutes: step.delay_minutes,
      channel: step.channel,
      template_id: String(step.template_id),
    });
  };

  const handleStepSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!activeAutomation) return;

    const payload = {
      step_order: stepForm.step_order,
      delay_minutes: stepForm.delay_minutes,
      channel: stepForm.channel,
      template_id: Number(stepForm.template_id),
    };

    if (editingStep) {
      await api.put(`${apiPrefix}/automation-steps/${editingStep.id}`, payload);
    } else {
      await api.post(`${apiPrefix}/automations/${activeAutomation.id}/steps`, payload);
    }

    setEditingStep(null);
    setStepForm(emptyStepForm);
    const data = await loadAutomations() as Automation[];
    const updated = data.find((item: Automation) => item.id === activeAutomation.id) || null;
    setActiveAutomation(updated);
  };

  const handleStepDelete = async (stepId: number) => {
    if (!window.confirm('Deseja remover esta etapa?')) return;
    await api.delete(`${apiPrefix}/automation-steps/${stepId}`);
    const data = await loadAutomations() as Automation[];
    const updated = data.find((item: Automation) => item.id === activeAutomation?.id) || null;
    setActiveAutomation(updated);
  };

  return (
    <>
      <PageHeader>
        <div>
          <Title>CRM</Title>
          <SectionDescription>Templates, segmentos, campanhas e automacoes para captar e reativar {audienceLabelPlural}.</SectionDescription>
        </div>
        <TabBar>
          <TabButton type="button" $active={activeTab === 'templates'} onClick={() => setActiveTab('templates')}>
            Templates
          </TabButton>
          <TabButton type="button" $active={activeTab === 'segments'} onClick={() => setActiveTab('segments')}>
            Segmentos
          </TabButton>
          <TabButton type="button" $active={activeTab === 'campaigns'} onClick={() => setActiveTab('campaigns')}>
            Campanhas
          </TabButton>
          <TabButton type="button" $active={activeTab === 'automations'} onClick={() => setActiveTab('automations')}>
            Automacoes
          </TabButton>
        </TabBar>
      </PageHeader>

      {activeTab === 'templates' && (
        <SectionCard>
          <SectionHeader>
            <div>
              <CardTitle>Templates</CardTitle>
              <SectionDescription>Base para campanhas e automacoes.</SectionDescription>
            </div>
            <PrimaryButton type="button" onClick={openNewTemplate}>Novo Template</PrimaryButton>
          </SectionHeader>
          <Filters>
            <SearchInput
              placeholder="Buscar template..."
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
            <Select value={channelFilter} onChange={event => setChannelFilter(event.target.value)}>
              <option value="all">Todos os canais</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="push">Push</option>
            </Select>
            <Select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
              <option value="all">Todos os status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </Select>
            <Select value={sortBy} onChange={event => setSortBy(event.target.value)}>
              <option value="name_asc">Titulo A-Z</option>
              <option value="name_desc">Titulo Z-A</option>
              <option value="date_desc">Data mais recente</option>
              <option value="date_asc">Data mais antiga</option>
            </Select>
          </Filters>
          <Table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Canal</th>
                <th>Status</th>
                <th>Data</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {pagedTemplates.map(template => (
                <tr key={template.id}>
                  <td>{template.name}</td>
                  <td>{template.channel}</td>
                  <td><StatusBadge $active={template.is_active}>{template.is_active ? 'Ativo' : 'Inativo'}</StatusBadge></td>
                  <td>{template.created_at ? new Date(template.created_at).toLocaleDateString('pt-BR') : '—'}</td>
                  <td>
                    <Actions>
                      <GhostButton type="button" onClick={() => openEditTemplate(template)}>Editar</GhostButton>
                      <GhostButton type="button" onClick={() => toggleActive(template)}>
                        {template.is_active ? 'Desativar' : 'Ativar'}
                      </GhostButton>
                      <DangerButton type="button" onClick={() => handleDelete(template.id)}>Excluir</DangerButton>
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {filteredTemplates.length > 0 && (
            <Pagination>
              <PageInfo>Pagina {currentPage} de {totalPages}</PageInfo>
              <PageControls>
                <PageButton
                  type="button"
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </PageButton>
                {pageNumbers.map((pageNumber, index) => (
                  pageNumber === -1 ? (
                    <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>
                  ) : (
                    <PageButton
                      key={pageNumber}
                      type="button"
                      $active={pageNumber === currentPage}
                      onClick={() => setPage(pageNumber)}
                    >
                      {pageNumber}
                    </PageButton>
                  )
                ))}
                <PageButton
                  type="button"
                  onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Proxima
                </PageButton>
                <PageSizeSelect value={pageSize} onChange={event => setPageSize(Number(event.target.value))}>
                  <option value={10}>10 / pagina</option>
                  <option value={20}>20 / pagina</option>
                  <option value={50}>50 / pagina</option>
                </PageSizeSelect>
              </PageControls>
            </Pagination>
          )}
        </SectionCard>
      )}

      {activeTab === 'segments' && (
        <>
          <InfoBanner>
            Crie segmentos para enviar mensagens para grupos especificos de {audienceLabelPlural}.
          </InfoBanner>
          <SectionCard>
            <SectionHeader>
              <div>
                <CardTitle>Segmentos</CardTitle>
                <SectionDescription>Defina quais {audienceLabelPlural} recebem campanhas e automacoes.</SectionDescription>
              </div>
              <PrimaryButton type="button" onClick={openNewSegment}>Novo Segmento</PrimaryButton>
            </SectionHeader>
            <Table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Regras</th>
                  <th>Status</th>
                  <th>Data</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {segments.map(segment => {
                  const rules = segment.rules || [];
                  const summary = rules.slice(0, 2).map(rule => rule.field).join(', ');
                  const more = rules.length > 2 ? ` +${rules.length - 2}` : '';
                  return (
                    <tr key={segment.id}>
                      <td>{segment.name}</td>
                      <td>{summary || 'Sem regras'}{more}</td>
                      <td><StatusBadge $active={segment.is_active}>{segment.is_active ? 'Ativo' : 'Inativo'}</StatusBadge></td>
                      <td>{segment.created_at ? new Date(segment.created_at).toLocaleDateString('pt-BR') : '—'}</td>
                      <td>
                        <Actions>
                          <GhostButton type="button" onClick={() => previewSegment(segment.id)}>Preview</GhostButton>
                          <GhostButton type="button" onClick={() => openEditSegment(segment)}>Editar</GhostButton>
                          <DangerButton type="button" onClick={() => handleSegmentDelete(segment.id)}>Excluir</DangerButton>
                        </Actions>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </SectionCard>
        </>
      )}

      {activeTab === 'campaigns' && (
        <>
          <InfoBanner>Campanhas enviam mensagens em massa. Para envio automatico, mantenha o scheduler ativo (php artisan schedule:work).</InfoBanner>
          <SectionCard>
            <SectionHeader>
              <div>
                <CardTitle>Campanhas</CardTitle>
                <SectionDescription>Envios manuais para todos, segmentos ou empresas especificas.</SectionDescription>
              </div>
              <PrimaryButton type="button" onClick={openNewCampaign}>Nova Campanha</PrimaryButton>
            </SectionHeader>
            <Table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Template</th>
                  <th>Segmento</th>
                  <th>Status</th>
                  <th>Envio</th>
                  <th>Resultados</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(campaign => (
                  <tr key={campaign.id}>
                    <td>{campaign.name}</td>
                    <td>{campaign.template?.name || '—'}</td>
                    <td>{campaign.customer_segment?.name || campaign.segment?.name || 'Todos'}</td>
                    <td>{campaign.status}</td>
                    <td>{campaign.scheduled_at ? new Date(campaign.scheduled_at).toLocaleString('pt-BR') : '—'}</td>
                    <td>
                      <div>Enviados: {campaign.sent_count ?? 0}/{campaign.total_recipients ?? 0}</div>
                      <div>
                        Aberturas: {campaign.open_count ?? 0}
                        {campaign.sent_count ? ` (${Math.round(((campaign.open_count ?? 0) / campaign.sent_count) * 100)}%)` : ''}
                      </div>
                      <div>
                        Cliques: {campaign.click_count ?? 0}
                        {campaign.sent_count ? ` (${Math.round(((campaign.click_count ?? 0) / campaign.sent_count) * 100)}%)` : ''}
                      </div>
                    </td>
                    <td>
                      <Actions>
                        <GhostButton type="button" onClick={() => openEditCampaign(campaign)}>Editar</GhostButton>
                        {campaign.status !== 'running' && (
                          <GhostButton
                            type="button"
                            onClick={() => scheduleCampaign(campaign.id)}
                          >
                            {campaign.status === 'draft' || campaign.status === 'scheduled'
                              ? 'Enviar agora'
                              : 'Reenviar agora'}
                          </GhostButton>
                        )}
                        <DangerButton type="button" onClick={() => handleCampaignDelete(campaign.id)}>Excluir</DangerButton>
                      </Actions>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </SectionCard>
        </>
      )}

      {activeTab === 'automations' && (
        <>
          <InfoBanner>
            {segmentAudience === 'customers'
              ? 'Automacoes para jornada de clientes finais (ex: inatividade de compra e aniversario). Mantenha o scheduler ativo para processar etapas.'
              : 'Automacoes disparam por evento (cadastro, login, inatividade). Mantenha o scheduler ativo para processar etapas.'}
          </InfoBanner>
          <SectionCard>
            <SectionHeader>
              <div>
                <CardTitle>Automacoes</CardTitle>
                <SectionDescription>Sequencias automaticas com etapas e atrasos.</SectionDescription>
              </div>
              <PrimaryButton type="button" onClick={openNewAutomation}>Nova Automacao</PrimaryButton>
            </SectionHeader>
            <Table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Gatilho</th>
                  <th>Status</th>
                  <th>Etapas</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {automations.map(automation => (
                  <tr key={automation.id}>
                    <td>{automation.name}</td>
                    <td>{automation.trigger_type}</td>
                    <td>{automation.status}</td>
                    <td>{automation.steps?.length || 0}</td>
                    <td>
                      <Actions>
                        <GhostButton type="button" onClick={() => openStepsModal(automation)}>Etapas</GhostButton>
                        <GhostButton type="button" onClick={() => openEditAutomation(automation)}>Editar</GhostButton>
                        <GhostButton type="button" onClick={() => toggleAutomationStatus(automation)}>
                          {automation.status === 'active' ? 'Pausar' : 'Ativar'}
                        </GhostButton>
                        <DangerButton type="button" onClick={() => handleAutomationDelete(automation.id)}>Excluir</DangerButton>
                      </Actions>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </SectionCard>
        </>
      )}

      {isTemplateModalOpen && (
        <>
          <ModalOverlay onClick={closeTemplateModal} />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{editingTemplate ? 'Editar Template' : 'Novo Template'}</ModalTitle>
              <CloseButton type="button" onClick={closeTemplateModal} aria-label="Fechar">
                ×
              </CloseButton>
            </ModalHeader>
            <Form onSubmit={handleTemplateSubmit}>
              <Section>
                <SectionTitle>Configuracao</SectionTitle>
                <FieldGrid>
                  <Field>
                    <Label>Nome</Label>
                    <Input
                      value={form.name}
                      onChange={event => setForm({ ...form, name: event.target.value })}
                      required
                    />
                  </Field>
                  <Field>
                    <Label>Canal</Label>
                    <Select
                      value={form.channel}
                      onChange={event => setForm({ ...form, channel: event.target.value })}
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="push">Push</option>
                    </Select>
                  </Field>
                  <Field $span={2}>
                    <Label>Assunto</Label>
                    <Input
                      value={form.subject}
                      onChange={event => setForm({ ...form, subject: event.target.value })}
                    />
                  </Field>
                  <Field>
                    <Label>Status</Label>
                    <Select
                      value={form.is_active ? 'active' : 'inactive'}
                      onChange={event => setForm({ ...form, is_active: event.target.value === 'active' })}
                    >
                      <option value="active">Ativo</option>
                      <option value="inactive">Inativo</option>
                    </Select>
                  </Field>
                </FieldGrid>
              </Section>
              <Section>
                <SectionTitle>Conteudo</SectionTitle>
                <FieldGrid>
                  <Field $span={2}>
                    <Label>HTML</Label>
                    <TextArea
                      value={form.body_html}
                      onChange={event => setForm({ ...form, body_html: event.target.value })}
                    />
                  </Field>
                  <Field $span={2}>
                    <Label>Texto (fallback)</Label>
                    <TextArea
                      value={form.body_text}
                      onChange={event => setForm({ ...form, body_text: event.target.value })}
                    />
                  </Field>
                </FieldGrid>
              </Section>
              <Section>
                <SectionTitle>Preview</SectionTitle>
                <PreviewBox>
                  <PreviewTitle>Preview</PreviewTitle>
                  {form.subject && <p><strong>Assunto:</strong> {form.subject}</p>}
                  <PreviewBody>
                    {form.body_html ? (
                      <div dangerouslySetInnerHTML={{ __html: form.body_html }} />
                    ) : (
                      <div>{form.body_text || 'Sem conteudo para visualizar.'}</div>
                    )}
                  </PreviewBody>
                </PreviewBox>
              </Section>
              <ButtonRow>
                <GhostButton type="button" onClick={closeTemplateModal}>Cancelar</GhostButton>
                <PrimaryButton type="submit">Salvar</PrimaryButton>
              </ButtonRow>
            </Form>
          </ModalContent>
        </>
      )}
      {isSegmentModalOpen && (
        <>
          <ModalOverlay onClick={closeSegmentModal} />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{editingSegment ? 'Editar Segmento' : 'Novo Segmento'}</ModalTitle>
              <CloseButton type="button" onClick={closeSegmentModal} aria-label="Fechar">
                ×
              </CloseButton>
            </ModalHeader>
            <Form onSubmit={handleSegmentSubmit}>
              <Section>
                <SectionTitle>Informacoes</SectionTitle>
                <FieldGrid>
                  <Field>
                    <Label>Nome</Label>
                    <Input
                      value={segmentForm.name}
                      onChange={event => setSegmentForm({ ...segmentForm, name: event.target.value })}
                      required
                    />
                  </Field>
                  <Field>
                    <Label>Status</Label>
                    <Select
                      value={segmentForm.is_active ? 'active' : 'inactive'}
                      onChange={event => setSegmentForm({ ...segmentForm, is_active: event.target.value === 'active' })}
                    >
                      <option value="active">Ativo</option>
                      <option value="inactive">Inativo</option>
                    </Select>
                  </Field>
                  <Field $span={2}>
                    <Label>Descricao</Label>
                    <TextArea
                      value={segmentForm.description}
                      onChange={event => setSegmentForm({ ...segmentForm, description: event.target.value })}
                    />
                  </Field>
                </FieldGrid>
              </Section>
              <Section>
                <SectionTitle>Regras</SectionTitle>
                <FieldGrid>
                  <Field $span={2}>
                    {segmentForm.rules.map((rule, index) => (
                      <RuleRow key={`rule-${index}`}>
                        <div>
                          <Label>Campo</Label>
                          <Select
                            value={rule.field}
                            onChange={event => {
                              const updated = [...segmentForm.rules];
                              updated[index] = { ...rule, field: event.target.value };
                              setSegmentForm({ ...segmentForm, rules: updated });
                            }}
                          >
                          {segmentRuleOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </Select>
                        </div>
                        <div>
                          <Label>Valor</Label>
                          {booleanRuleFields.includes(rule.field) ? (
                            <Select
                              value={rule.value || 'true'}
                              onChange={event => {
                                const updated = [...segmentForm.rules];
                                updated[index] = { ...rule, value: event.target.value };
                                setSegmentForm({ ...segmentForm, rules: updated });
                              }}
                            >
                              <option value="true">Sim</option>
                              <option value="false">Nao</option>
                            </Select>
                          ) : (
                            <Input
                              value={rule.value}
                              onChange={event => {
                                const updated = [...segmentForm.rules];
                                updated[index] = { ...rule, value: event.target.value };
                                setSegmentForm({ ...segmentForm, rules: updated });
                              }}
                              placeholder="Ex: 7 ou 1,2,3"
                            />
                          )}
                        </div>
                        <TinyButton
                          type="button"
                          onClick={() => {
                            const updated = segmentForm.rules.filter((_, idx) => idx !== index);
                            setSegmentForm({ ...segmentForm, rules: updated.length ? updated : [{ field: defaultSegmentField, value: '' }] });
                          }}
                        >
                          Remover
                        </TinyButton>
                      </RuleRow>
                    ))}
                    <TinyButton
                      type="button"
                      onClick={() => setSegmentForm({
                        ...segmentForm,
                        rules: [...segmentForm.rules, { field: defaultSegmentField, value: '' }],
                      })}
                    >
                      Adicionar regra
                    </TinyButton>
                  </Field>
                </FieldGrid>
              </Section>
              <ButtonRow>
                <GhostButton type="button" onClick={closeSegmentModal}>Cancelar</GhostButton>
                <PrimaryButton type="submit">Salvar</PrimaryButton>
              </ButtonRow>
            </Form>
          </ModalContent>
        </>
      )}

      {isCampaignModalOpen && (
        <>
          <ModalOverlay onClick={closeCampaignModal} />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{editingCampaign ? 'Editar Campanha' : 'Nova Campanha'}</ModalTitle>
              <CloseButton type="button" onClick={closeCampaignModal} aria-label="Fechar">
                ×
              </CloseButton>
            </ModalHeader>
            <Form onSubmit={handleCampaignSubmit}>
              <Section>
                <SectionTitle>Configuracao</SectionTitle>
                <FieldGrid>
                  <Field>
                    <Label>Nome</Label>
                    <Input
                      value={campaignForm.name}
                      onChange={event => setCampaignForm({ ...campaignForm, name: event.target.value })}
                      required
                    />
                  </Field>
                  <Field>
                    <Label>Canal</Label>
                    <Select
                      value={campaignForm.channel}
                      onChange={event => setCampaignForm({ ...campaignForm, channel: event.target.value })}
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="push">Push</option>
                    </Select>
                  </Field>
                  <Field $span={2}>
                    <Label>Template</Label>
                    <Select
                      value={campaignForm.template_id}
                      onChange={event => setCampaignForm({ ...campaignForm, template_id: event.target.value })}
                    >
                      <option value="">Selecione</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>{template.name}</option>
                      ))}
                    </Select>
                  </Field>
                </FieldGrid>
              </Section>
              <Section>
                <SectionTitle>Publico</SectionTitle>
                <FieldGrid>
                  <Field>
                    <Label>Enviar para</Label>
                    <Select
                      value={campaignForm.audience_type}
                      onChange={event => setCampaignForm({ ...campaignForm, audience_type: event.target.value })}
                    >
                      <option value="all">Todos os {audienceLabelPlural}</option>
                      <option value="segment">Segmento</option>
                      {allowCompanySelection && segmentAudience === 'users' && <option value="companies">Empresas especificas</option>}
                    </Select>
                  </Field>
                  {campaignForm.audience_type === 'segment' && (
                    <Field>
                      <Label>Segmento</Label>
                      <Select
                        value={campaignForm.segment_id}
                        onChange={event => setCampaignForm({ ...campaignForm, segment_id: event.target.value })}
                      >
                        <option value="">Selecione</option>
                        {segments.map(segment => (
                          <option key={segment.id} value={segment.id}>{segment.name}</option>
                        ))}
                      </Select>
                    </Field>
                  )}
                  {campaignForm.audience_type === 'companies' && (
                    <Field $span={2}>
                      <Label>Empresas selecionadas ({selectedCompanyIds.length})</Label>
                      <Input
                        placeholder="Buscar empresa..."
                        value={companySearch}
                        onChange={event => setCompanySearch(event.target.value)}
                      />
                      <CompanyList>
                        {companies.map(company => (
                          <CompanyItem key={company.id}>
                            <input
                              type="checkbox"
                              checked={selectedCompanyIds.includes(company.id)}
                              onChange={() => {
                                setSelectedCompanyIds(prev => prev.includes(company.id)
                                  ? prev.filter(id => id !== company.id)
                                  : [...prev, company.id]
                                );
                              }}
                            />
                            <CompanyMeta>
                              <strong>{company.name}</strong>
                              <span>{company.cnpj || company.email || 'Sem contato'}</span>
                            </CompanyMeta>
                          </CompanyItem>
                        ))}
                      </CompanyList>
                    </Field>
                  )}
                </FieldGrid>
              </Section>
              <Section>
                <SectionTitle>Envio</SectionTitle>
                <FieldGrid>
                  <Field>
                    <Label>Enviar agora</Label>
                    <Select
                      value={campaignForm.send_now ? 'yes' : 'no'}
                      onChange={event => setCampaignForm({ ...campaignForm, send_now: event.target.value === 'yes' })}
                    >
                      <option value="yes">Sim</option>
                      <option value="no">Agendar</option>
                    </Select>
                  </Field>
                  {!campaignForm.send_now && (
                    <Field>
                      <Label>Agendar para</Label>
                      <Input
                        type="datetime-local"
                        value={campaignForm.scheduled_at}
                        onChange={event => setCampaignForm({ ...campaignForm, scheduled_at: event.target.value })}
                      />
                    </Field>
                  )}
                </FieldGrid>
              </Section>
              <ButtonRow>
                <GhostButton type="button" onClick={closeCampaignModal}>Cancelar</GhostButton>
                <PrimaryButton type="submit">Enviar</PrimaryButton>
              </ButtonRow>
            </Form>
          </ModalContent>
        </>
      )}

      {isAutomationModalOpen && (
        <>
          <ModalOverlay onClick={closeAutomationModal} />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{editingAutomation ? 'Editar Automacao' : 'Nova Automacao'}</ModalTitle>
              <CloseButton type="button" onClick={closeAutomationModal} aria-label="Fechar">
                ×
              </CloseButton>
            </ModalHeader>
            <Form onSubmit={handleAutomationSubmit}>
              <Section>
                <SectionTitle>Configuracao</SectionTitle>
                <FieldGrid>
                  <Field>
                    <Label>Nome</Label>
                    <Input
                      value={automationForm.name}
                      onChange={event => setAutomationForm({ ...automationForm, name: event.target.value })}
                      required
                    />
                  </Field>
                  <Field>
                    <Label>Status</Label>
                    <Select
                      value={automationForm.status}
                      onChange={event => setAutomationForm({ ...automationForm, status: event.target.value })}
                    >
                      <option value="active">Ativo</option>
                      <option value="paused">Pausado</option>
                      <option value="archived">Arquivado</option>
                    </Select>
                  </Field>
                  <Field $span={2}>
                    <Label>Gatilho</Label>
                    <Select
                      value={automationForm.trigger_type}
                      onChange={event => setAutomationForm({ ...automationForm, trigger_type: event.target.value })}
                    >
                      {automationTriggerOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Select>
                  </Field>
                  {automationForm.trigger_type === 'inactivity' && (
                    <Field>
                      <Label>{segmentAudience === 'customers' ? 'Dias sem compra' : 'Dias sem login'}</Label>
                      <Input
                        value={automationForm.days_inactive}
                        onChange={event => setAutomationForm({ ...automationForm, days_inactive: event.target.value })}
                      />
                    </Field>
                  )}
                  {automationForm.trigger_type === 'trial_ending' && segmentAudience === 'users' && (
                    <Field>
                      <Label>Dias antes do fim</Label>
                      <Input
                        value={automationForm.days_before}
                        onChange={event => setAutomationForm({ ...automationForm, days_before: event.target.value })}
                      />
                    </Field>
                  )}
                  <Field $span={2}>
                    <Label>Restringir por segmento (opcional)</Label>
                    <Select
                      value={automationForm.segment_id}
                      onChange={event => setAutomationForm({ ...automationForm, segment_id: event.target.value })}
                    >
                      <option value="">Nenhum</option>
                      {segments.map(segment => (
                        <option key={segment.id} value={segment.id}>{segment.name}</option>
                      ))}
                    </Select>
                  </Field>
                  {automationForm.trigger_type === 'custom' && segmentAudience === 'users' && (
                    <Field $span={2}>
                      <Label>Configuracao JSON</Label>
                      <TextArea
                        value={automationForm.custom_json}
                        onChange={event => setAutomationForm({ ...automationForm, custom_json: event.target.value })}
                      />
                    </Field>
                  )}
                  {automationForm.trigger_type === 'custom' && segmentAudience === 'customers' && (
                    <Field $span={2}>
                      <Label>Evento do cliente</Label>
                      <Select
                        value={automationForm.customer_event}
                        onChange={event => setAutomationForm({ ...automationForm, customer_event: event.target.value })}
                      >
                        <option value="customer_birthday">Aniversario do cliente</option>
                        <option value="customer_registered">Cadastro do cliente</option>
                        <option value="customer_purchase">Compra registrada</option>
                        <option value="customer_first_purchase">Primeira compra</option>
                        <option value="customer_second_purchase">Segunda compra</option>
                        <option value="customer_redeemed_reward">Resgate de recompensa</option>
                        <option value="customer_points_expired">Expiracao de pontos</option>
                      </Select>
                    </Field>
                  )}
                </FieldGrid>
              </Section>
              <ButtonRow>
                <GhostButton type="button" onClick={closeAutomationModal}>Cancelar</GhostButton>
                <PrimaryButton type="submit">Salvar</PrimaryButton>
              </ButtonRow>
            </Form>
          </ModalContent>
        </>
      )}

      {isStepsModalOpen && activeAutomation && (
        <>
          <ModalOverlay onClick={closeStepsModal} />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Etapas - {activeAutomation.name}</ModalTitle>
              <CloseButton type="button" onClick={closeStepsModal} aria-label="Fechar">
                ×
              </CloseButton>
            </ModalHeader>
            <Section>
              <SectionTitle>Etapas atuais</SectionTitle>
              <Table>
                <thead>
                  <tr>
                    <th>Ordem</th>
                    <th>Delay (min)</th>
                    <th>Canal</th>
                    <th>Template</th>
                    <th>Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {activeAutomation.steps.map(step => (
                    <tr key={step.id}>
                      <td>{step.step_order}</td>
                      <td>{step.delay_minutes}</td>
                      <td>{step.channel}</td>
                      <td>{templates.find(template => template.id === step.template_id)?.name || step.template_id}</td>
                      <td>
                        <Actions>
                          <GhostButton type="button" onClick={() => openEditStep(step)}>Editar</GhostButton>
                          <DangerButton type="button" onClick={() => handleStepDelete(step.id)}>Excluir</DangerButton>
                        </Actions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Section>
            <Form onSubmit={handleStepSubmit}>
              <Section>
                <SectionTitle>{editingStep ? 'Editar etapa' : 'Nova etapa'}</SectionTitle>
                <FieldGrid>
                  <Field>
                    <Label>Ordem</Label>
                    <Input
                      type="number"
                      value={stepForm.step_order}
                      onChange={event => setStepForm({ ...stepForm, step_order: Number(event.target.value) })}
                    />
                  </Field>
                  <Field>
                    <Label>Delay (min)</Label>
                    <Input
                      type="number"
                      value={stepForm.delay_minutes}
                      onChange={event => setStepForm({ ...stepForm, delay_minutes: Number(event.target.value) })}
                    />
                  </Field>
                  <Field>
                    <Label>Canal</Label>
                    <Select
                      value={stepForm.channel}
                      onChange={event => setStepForm({ ...stepForm, channel: event.target.value })}
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="push">Push</option>
                    </Select>
                  </Field>
                  <Field>
                    <Label>Template</Label>
                    <Select
                      value={stepForm.template_id}
                      onChange={event => setStepForm({ ...stepForm, template_id: event.target.value })}
                    >
                      <option value="">Selecione</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>{template.name}</option>
                      ))}
                    </Select>
                  </Field>
                </FieldGrid>
              </Section>
              <ButtonRow>
                <GhostButton type="button" onClick={() => {
                  setEditingStep(null);
                  setStepForm(emptyStepForm);
                }}>
                  Limpar
                </GhostButton>
                <PrimaryButton type="submit">{editingStep ? 'Atualizar' : 'Adicionar'}</PrimaryButton>
              </ButtonRow>
            </Form>
          </ModalContent>
        </>
      )}
    </>
  );
};
